class HomeReviewsCarousel {
  constructor(section) {
    this.section = section;
    this.app = section.querySelector('[data-reviews-app]');
    this.controls = section.querySelector('[data-reviews-controls]');
    this.previousButton = section.querySelector('[data-reviews-previous]');
    this.nextButton = section.querySelector('[data-reviews-next]');
    this.toggleButton = section.querySelector('[data-reviews-toggle]');
    this.toggleLabel = section.querySelector('[data-reviews-toggle-label]');
    this.liveRegion = section.querySelector('[data-reviews-live]');
    this.maxItems = Number(section.dataset.maxItems) || 6;
    this.autoplayDelay = Number(section.dataset.autoplayDelay) || 5000;
    this.autoplayEnabled = section.dataset.autoplay === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = !this.autoplayEnabled || this.reducedMotion.matches;
    this.temporaryPause = false;
    this.timer = null;
    this.moving = false;
    this.touchStartX = null;
    this.observer = null;
    this.mountTimeout = null;
    this.tryMount = this.tryMount.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  init() {
    if (!this.app || this.section.dataset.reviewsInitialized === 'true') return;

    this.section.dataset.reviewsInitialized = 'true';
    this.observer = new MutationObserver(this.tryMount);
    this.observer.observe(this.app, { childList: true, subtree: true });
    this.tryMount();
    this.mountTimeout = window.setTimeout(() => this.observer?.disconnect(), 15000);
  }

  tryMount() {
    const root = this.app.querySelector('#rlt-cont');
    const track = root?.querySelector('.gridTestimoniales');
    if (!track || track.children.length === 0) return;

    const preferredCards = Array.from(track.children).filter((card) => card.classList.contains('p-test-0'));
    if (preferredCards.length < this.maxItems && track.children.length < this.maxItems) return;
    const cards = (preferredCards.length ? preferredCards : Array.from(track.children)).slice(0, this.maxItems);
    if (cards.length < 2) return;

    this.observer?.disconnect();
    window.clearTimeout(this.mountTimeout);
    this.track = track;
    this.cards = cards;

    Array.from(track.children).forEach((card) => {
      if (!cards.includes(card)) card.remove();
    });

    this.prepareCards();
    this.bindEvents();
    this.section.classList.add('home-reviews--ready');
    this.controls.hidden = false;
    this.track.setAttribute('role', 'list');
    this.updateAccessibility();
    this.updateToggle();
    this.schedule();
  }

  prepareCards() {
    this.cards.forEach((card, index) => {
      card.setAttribute('role', 'listitem');
      card.dataset.reviewIndex = String(index + 1);

      const author = card.querySelector('.tm-rev-cus')?.textContent.trim() || 'una clienta Morkas';
      const product = card.querySelector('.tm-revie-prod')?.textContent.trim() || 'un producto Morkas';

      card.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
        if (!image.alt) image.alt = `Foto compartida por ${author} sobre ${product}`;
      });

      const comment = card.querySelector('.tm-rev-com');
      if (!comment || comment.textContent.trim().length < 220) return;

      const button = document.createElement('button');
      const commentId = `ReviewComment-${this.section.id}-${index + 1}`;
      comment.id = commentId;
      button.type = 'button';
      button.className = 'home-reviews__read-more';
      button.textContent = 'Leer más';
      button.setAttribute('aria-controls', commentId);
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        button.textContent = expanded ? 'Leer más' : 'Leer menos';
        comment.classList.toggle('is-expanded', !expanded);
        this.pauseTemporarily();
      });
      comment.insertAdjacentElement('afterend', button);
    });
  }

  bindEvents() {
    this.nextButton.addEventListener('click', () => {
      this.userPaused = true;
      this.updateToggle();
      this.next();
    });
    this.previousButton.addEventListener('click', () => {
      this.userPaused = true;
      this.updateToggle();
      this.previous();
    });
    this.toggleButton.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.updateToggle();
      this.schedule();
    });

    this.section.addEventListener('mouseenter', () => this.pauseTemporarily());
    this.section.addEventListener('mouseleave', () => this.resumeTemporarily());
    this.section.addEventListener('focusin', () => this.pauseTemporarily());
    this.section.addEventListener('focusout', (event) => {
      if (!this.section.contains(event.relatedTarget)) this.resumeTemporarily();
    });
    this.track.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      this.pauseTemporarily();
    }, { passive: true });
    this.track.addEventListener('touchend', (event) => {
      const touchEndX = event.changedTouches[0]?.clientX;
      if (this.touchStartX !== null && touchEndX !== undefined) {
        const distance = touchEndX - this.touchStartX;
        if (Math.abs(distance) > 40) distance < 0 ? this.next() : this.previous();
      }
      this.touchStartX = null;
      this.resumeTemporarily();
    }, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  getStep() {
    const first = this.track.children[0];
    const second = this.track.children[1];
    if (!first || !second) return 0;
    return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
  }

  next() {
    if (this.moving) return;
    const step = this.getStep();
    if (!step) return;
    this.moving = true;
    this.clearTimer();
    this.track.style.transition = '';
    this.track.style.transform = `translate3d(-${step}px, 0, 0)`;
    window.setTimeout(() => {
      this.track.append(this.track.firstElementChild);
      this.track.style.transition = 'none';
      this.track.style.transform = 'translate3d(0, 0, 0)';
      void this.track.offsetWidth;
      this.track.style.transition = '';
      this.moving = false;
      this.updateAccessibility();
      this.schedule();
    }, this.reducedMotion.matches ? 0 : 520);
  }

  previous() {
    if (this.moving) return;
    const step = this.getStep();
    if (!step) return;
    this.moving = true;
    this.clearTimer();
    this.track.prepend(this.track.lastElementChild);
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(-${step}px, 0, 0)`;
    void this.track.offsetWidth;
    this.track.style.transition = '';
    this.track.style.transform = 'translate3d(0, 0, 0)';
    window.setTimeout(() => {
      this.moving = false;
      this.updateAccessibility();
      this.schedule();
    }, this.reducedMotion.matches ? 0 : 520);
  }

  visibleCount() {
    if (window.matchMedia('(min-width: 750px)').matches) {
      return Number(getComputedStyle(this.section).getPropertyValue('--home-reviews-cards-desktop')) || 3;
    }
    return 1;
  }

  updateAccessibility() {
    const visible = this.visibleCount();
    Array.from(this.track.children).forEach((card, index) => {
      const hidden = index >= visible;
      card.setAttribute('aria-hidden', String(hidden));
      card.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((control) => {
        if (hidden) {
          if (!control.hasAttribute('data-review-tabindex')) {
            control.setAttribute('data-review-tabindex', control.getAttribute('tabindex') ?? '');
          }
          control.setAttribute('tabindex', '-1');
        } else if (control.hasAttribute('data-review-tabindex')) {
          const originalTabindex = control.getAttribute('data-review-tabindex');
          originalTabindex ? control.setAttribute('tabindex', originalTabindex) : control.removeAttribute('tabindex');
          control.removeAttribute('data-review-tabindex');
        }
      });
    });
    const current = this.track.firstElementChild?.dataset.reviewIndex || '1';
    this.liveRegion.textContent = `Mostrando reseña ${current} de ${this.cards.length}`;
  }

  updateToggle() {
    if (!this.autoplayEnabled || this.reducedMotion.matches) {
      this.toggleButton.hidden = true;
      return;
    }
    this.toggleButton.hidden = false;
    this.toggleButton.setAttribute('aria-label', this.userPaused ? 'Reanudar carrusel de reseñas' : 'Pausar carrusel de reseñas');
    this.toggleLabel.textContent = this.userPaused ? 'Reanudar' : 'Pausar';
  }

  pauseTemporarily() {
    this.temporaryPause = true;
    this.clearTimer();
  }

  resumeTemporarily() {
    this.temporaryPause = false;
    this.schedule();
  }

  onVisibilityChange() {
    document.hidden ? this.pauseTemporarily() : this.resumeTemporarily();
  }

  clearTimer() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  schedule() {
    this.clearTimer();
    if (this.userPaused || this.temporaryPause || this.reducedMotion.matches || document.hidden) return;
    this.timer = window.setTimeout(() => this.next(), this.autoplayDelay);
  }
}

const initializeHomeReviews = (root = document) => {
  const sections = root.matches?.('[data-home-reviews]') ? [root] : root.querySelectorAll('[data-home-reviews]');
  sections.forEach((section) => new HomeReviewsCarousel(section).init());
};

document.addEventListener('DOMContentLoaded', () => initializeHomeReviews());
document.addEventListener('shopify:section:load', (event) => initializeHomeReviews(event.target));
